import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Leaf, Upload, FileSpreadsheet, AlertCircle, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { FileUploader } from '../components/membership/FileUploader';
import { ProcessingLog } from '../components/membership/ProcessingLog';
import { ProcessingStats } from '../components/membership/ProcessingStats';

interface MembershipPayment {
  external_id: string;
  membership_name: string;
  invoice_number: string;
  payment_date: string;
  amount: number;
  payment_method: string;
}

interface ProcessingStats {
  total: number;
  processed: number;
  skipped: number;
  errors: number;
}

type LogType = 'info' | 'error' | 'success';

interface LogEntry {
  type: LogType;
  message: string;
  timestamp: Date;
}

export function MembershipPaymentsImport() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<MembershipPayment[]>([]);

  const addLog = (type: LogType, message: string) => {
    setLogs(prev => [...prev, { type, message, timestamp: new Date() }]);
  };

  const parseMembershipPaymentsFlexible = (rawRows: any[][]): MembershipPayment[] => {
    const payments: MembershipPayment[] = [];
    const grouped = new Map<string, any[][]>();

    console.log('Total raw rows:', rawRows.length);
    for (const row of rawRows) {
      console.log('Invoice:', row[1]);
      const invoice = row[1]?.toString().trim();
      if (!invoice || !invoice.endsWith('M')) continue;
      if (!grouped.has(invoice)) grouped.set(invoice, []);
      grouped.get(invoice)!.push(row);
    }

    for (const [invoice_number, rows] of grouped.entries()) {
      const serviceRow = rows.find(row => row[4]?.toString().trim());
      console.log('Checking Amount Column M:', rows.map(r => r[12]));
      const paymentRow = rows.find(row => parseFloat(row[12]) > 0);

      if (!serviceRow || !paymentRow) continue;

      const external_id = serviceRow[2]?.toString().trim();
      const membership_name = serviceRow[4]?.toString().trim();
      const payment_method = paymentRow[13]?.toString().trim();
      const amount = parseFloat(paymentRow[12]?.toString().replace(/[$,]/g, '') || '0');
      const rawDate = paymentRow[0];

      let payment_date = '';
      if (rawDate instanceof Date) {
        payment_date = rawDate.toISOString().split('T')[0];
      } else if (typeof rawDate === 'number') {
        const excelEpoch = new Date((rawDate - 25569) * 86400 * 1000);
        payment_date = excelEpoch.toISOString().split('T')[0];
      } else if (typeof rawDate === 'string') {
        const [yyyy, mm, dd] = rawDate.split(/[-\/]/);
        if (yyyy && mm && dd) {
          payment_date = `${yyyy.padStart(4, '0')}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
        }
      }
      

      console.log('Parsed Fields:', { external_id, membership_name, amount, payment_date });
      if (external_id && membership_name && !isNaN(amount) && payment_date) {
        payments.push({
          external_id,
          membership_name,
          invoice_number,
          payment_date,
          amount: Math.abs(amount),
          payment_method: payment_method || 'unspecified'
        });
      }
    }

    return payments;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const parsed = parseMembershipPaymentsFlexible(rawRows.slice(1));
        setPreview(parsed);
        addLog('info', `Parsed ${parsed.length} valid payment records.`);
      } catch (error: any) {
        addLog('error', `Failed to parse file: ${error.message}`);
      }
    };

    reader.readAsBinaryString(file);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate('/settings')}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Import Membership Payments</h1>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-12 text-center">
                <Leaf className="h-12 w-12 text-white opacity-20 mx-auto" />
                <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                <p className="text-green-100 text-lg mb-4">
                  Tracking Health and Reducing Illness Via Efficiency
                </p>
                <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                <h2 className="text-2xl font-semibold text-white mt-4">Membership Payments Import</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">Import Instructions</h2>
                <p className="text-sm text-gray-500">
                  Upload an Excel file exported from Aesthetics Pro's <strong>Invoice Line Item Report</strong>.
                </p>
                <ul className="list-disc list-inside text-sm text-gray-500 mt-2 space-y-1">
                  <li>File must be in Excel format (.xlsx)</li>
                  <li>The file should include invoice details and payment records</li>
                  <li>Each invoice line should be paired with a corresponding payment line</li>
                  <li>Ensure headers are not removed or re-ordered</li>
                  <li>We use Invoice # and CID to match to existing records</li>
                </ul>
              </div>

              <FileUploader onDrop={onDrop} />

              {preview.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Parsed Payments</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Invoice</th>
                          <th className="px-4 py-2 text-left">CID</th>
                          <th className="px-4 py-2 text-left">Membership</th>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Method</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {preview.map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 whitespace-nowrap">{row.invoice_number}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{row.external_id}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{row.membership_name}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{row.payment_date}</td>
                            <td className="px-4 py-2 whitespace-nowrap">${row.amount.toFixed(2)}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{row.payment_method}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
    </div>

<div className="mt-4 flex justify-end space-x-4">
  <button
    onClick={() => setPreview([])}
    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
  >
    Clear Preview
  </button>
  <button
    onClick={async () => {
      setLoading(true);
      let processed = 0;
      let updated = 0;
      let errors = 0;
      let skipped = 0;

      try {
        // Removed client-side staging cleanup; now handled in backend function
        const { error: insertError } = await supabase
          .from('membership_payments_staging')
          .insert(preview);

        if (insertError) {
          addLog('error', `Failed to insert into staging: ${insertError.message}`);
          setLoading(false);
          return;
        }

        const { data: rpcResult, error: rpcError } = await supabase.rpc('process_staged_membership_payments');

        if (rpcError) {
          addLog('error', `Processing error: ${rpcError.message}`);
        } else {
          addLog('success', `Processed payments: Inserted: ${rpcResult.inserted}, Updated: ${rpcResult.updated}, Skipped: ${rpcResult.skipped}`);
          setStats({
            total: preview.length,
            processed: rpcResult.inserted,
            skipped: rpcResult.skipped,
            errors: 0
          });
        }
      } catch (err: any) {
        addLog('error', `Unexpected error: ${err.message}`);
      }

      setLoading(false);
      setPreview([]);
      if (rpcResult?.inserted > 0 || rpcResult?.skipped > 0) {
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50';
        modal.innerHTML = `
          <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md text-center">
            <h2 class="text-xl font-semibold text-gray-800 mb-2">Import Summary</h2>
            <p class="text-sm text-gray-600 mb-4">
              Inserted: ${rpcResult?.inserted}<br/>
              Updated: ${rpcResult?.updated}<br/>
              Skipped: ${rpcResult?.skipped}<br/>
              Errors: 0
            </p>
            <button class="mt-2 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md" onclick="this.closest('.fixed').remove()">
              Close
            </button>
          </div>
        `;
        document.body.appendChild(modal);
      }
      

      addLog('success', `Import complete. Inserted: ${processed}, Updated: ${updated}, Skipped: ${skipped}, Errors: ${errors}`);
      setLoading(false);
      

      }
    }
    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
    disabled={loading}
  >
    {loading ? 'Importing...' : 'Continue Import'}
  </button>
</div>
</div>
              )}

              <ProcessingLog logs={logs} />
              <ProcessingStats stats={stats} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
